/**
 * <gh-newsletter-popup> — newsletter pop-up (Gaucho House, sections/gh-newsletter-popup.liquid).
 *
 * - Opens `data-delay` seconds after the page loads, on the devices set in `data-devices`.
 *   On the cart page (`data-exit-intent`) it waits for the pointer to leave the top of the window.
 * - Closing it (X, "No, thank you", Esc, a click on the overlay) hides it for `data-repeat-hours`
 *   (0 = shows again on the next page). Once someone subscribes it never opens again.
 * - After Shopify's customer form posts back (URL ends in #<form id>), it opens at once with the
 *   coupon, or with the error if the email was rejected.
 * - In the theme editor it opens when the section is selected, in the state chosen in its settings.
 * - Native modal <dialog>: focus stays inside and the page behind doesn't scroll (Horizon's lockScroll).
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const DISMISSED_KEY = 'gh-newsletter-popup:dismissed-at';
const SUBSCRIBED_KEY = 'gh-newsletter-popup:subscribed';
const MOBILE_QUERY = '(max-width: 749px)';
const COPIED_RESET_MS = 2500;

/** localStorage can throw (private windows, blocked storage): the pop-up still works without it */
const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
};

class GhNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-gh-npopup-dialog]');
    this.formState = this.querySelector('[data-gh-npopup-state="form"]');
    this.successState = this.querySelector('[data-gh-npopup-state="success"]');
    if (!this.dialog || !this.formState || !this.successState) return;

    this.onClick = this.onClick.bind(this);
    this.onCancel = (event) => {
      event.preventDefault();
      this.close();
    };
    this.onDialogClose = () => unlockScroll(this);
    this.onExitIntent = (event) => {
      if (event.relatedTarget || event.clientY > 0) return;
      document.documentElement.removeEventListener('mouseout', this.onExitIntent);
      this.open();
    };
    this.onSectionSelect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.open();
    };
    this.onSectionDeselect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.close(false);
    };

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onDialogClose);
    if (this.dataset.hideVitals === 'true') this.watchVitals();

    if (window.Shopify?.designMode) {
      this.showState(this.dataset.editorState === 'success' ? 'success' : 'form');
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      return;
    }

    // Back from the form post: show the coupon or the error right away
    if (window.location.hash === `#${this.dataset.formId}`) {
      if (this.successState.dataset.posted === 'true') {
        storage.set(SUBSCRIBED_KEY, '1');
        this.showState('success');
        this.open();
        return;
      }
      if (this.successState.dataset.errors === 'true') {
        this.showState('form');
        this.open();
        return;
      }
    }

    if (!this.shouldShow()) return;

    if (this.dataset.exitIntent === 'true' && !window.matchMedia(MOBILE_QUERY).matches) {
      document.documentElement.addEventListener('mouseout', this.onExitIntent);
      return;
    }
    if (this.dataset.exitIntent === 'true') return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.timer = window.setTimeout(() => this.open(), delay);
  }

  /**
   * The Vitals discount pop-up is a modal <dialog>. Hidden with CSS it stays open in the top layer
   * and, being a modal, makes the rest of the page inert: this pop-up's X stopped responding until
   * Esc had closed it. Close it whenever Vitals opens it (while the app still has it on).
   */
  watchVitals() {
    const closeVitals = () => {
      document.querySelectorAll('#vitals_popup dialog[open]').forEach((dialog) => dialog.close());
    };
    const watchRoot = (root) => {
      this.vitalsObserver?.disconnect();
      this.vitalsObserver = new MutationObserver(closeVitals);
      this.vitalsObserver.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
      closeVitals();
    };

    const root = document.getElementById('vitals_popup');
    if (root) {
      watchRoot(root);
      return;
    }
    // The app adds its container after the page loads: wait for it among the body's children
    this.vitalsObserver = new MutationObserver(() => {
      const added = document.getElementById('vitals_popup');
      if (added) watchRoot(added);
    });
    this.vitalsObserver.observe(document.body, { childList: true });
  }

  disconnectedCallback() {
    this.vitalsObserver?.disconnect();
    window.clearTimeout(this.timer);
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('cancel', this.onCancel);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    document.documentElement.removeEventListener('mouseout', this.onExitIntent);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    unlockScroll(this);
  }

  shouldShow() {
    if (storage.get(SUBSCRIBED_KEY)) return false;

    const devices = this.dataset.devices;
    const mobile = window.matchMedia(MOBILE_QUERY).matches;
    if ((devices === 'desktop' && mobile) || (devices === 'mobile' && !mobile)) return false;

    const dismissedAt = Number(storage.get(DISMISSED_KEY));
    const repeatMs = Math.max(0, Number(this.dataset.repeatHours) || 0) * 3600 * 1000;
    return !dismissedAt || Date.now() - dismissedAt >= repeatMs;
  }

  /** @param {'form' | 'success'} state */
  showState(state) {
    const success = state === 'success';
    this.formState.hidden = success;
    this.successState.hidden = !success;
    const heading = (success ? this.successState : this.formState).querySelector('.gh-npopup__heading');
    if (heading?.id) this.dialog.setAttribute('aria-labelledby', heading.id);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    // Another dialog (size guide, cart, search) is open: try again later instead of stacking
    if (document.querySelector('dialog[open]')) {
      this.timer = window.setTimeout(() => this.open(), 5000);
      return;
    }
    this.dialog.showModal();
    lockScroll(this);
  }

  /** @param {boolean} [remember] Remember the dismissal (not when the editor deselects the section) */
  close(remember = true) {
    if (!this.dialog?.open) return;
    if (remember && !window.Shopify?.designMode) storage.set(DISMISSED_KEY, String(Date.now()));
    this.dialog.close();
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target === this.dialog || target.closest('[data-gh-npopup-close]')) {
      this.close();
      return;
    }

    if (target.closest('[data-gh-npopup-copy]')) this.copyCode();
  }

  async copyCode() {
    const code = this.querySelector('[data-gh-npopup-code]')?.textContent?.trim();
    const message = this.querySelector('[data-gh-npopup-copied]');
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // No clipboard access: select the code so it can be copied by hand
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-gh-npopup-code]'));
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
      return;
    }
    if (!message) return;
    message.textContent = message.dataset.message || '';
    window.clearTimeout(this.copiedTimer);
    this.copiedTimer = window.setTimeout(() => (message.textContent = ''), COPIED_RESET_MS);
  }
}

if (!customElements.get('gh-newsletter-popup')) customElements.define('gh-newsletter-popup', GhNewsletterPopup);
